import type { Messages } from '../messages.js'

/**
 * Hebrew, and the first locale that reads right to left.
 *
 * `מהלכים` for flips, the move you spend, which is the shape Dutch set with `zetten`.
 * `אריחים` for tiles and `לוח` for the board.
 *
 * Written unpointed, as Hebrew has been for centuries and as the tiles are: niqqud are marks
 * rather than letters and the alphabet strips them, so a pointed interface over an unpointed
 * board would be two spellings of the same game.
 */
export const he: Messages = {
  tag: 'he',

  readingDictionary: 'קורא את המילון…',
  noWordList: 'אין רשימת מילים ל־"{language}". צרו אחת:  pnpm dictionary build',
  emptyWordList: 'רשימת המילים ל־"{language}" ריקה.',

  flips: 'מהלכים',
  score: 'ניקוד',
  words: 'מילים',
  round: 'סיבוב',
  ticksLeftLabel: 'הזמן שנותר בסיבוב הזה',
  typeAWord: 'הקלידו מילה',
  tapPrompt: 'הקישו על אותיות כדי לבחור או להחזיר, ואז {action}',

  boardOfTiles: 'לוח של {n} אריחים',
  faceDown: 'הפוך',
  wildCard: "ג'וקר",
  wildKey: 'כל אות',
  letterReplaced: '{from} הפכה ל־{to}',
  letterSwap: 'החלפת אות!',
  spentTile: 'אריח מנוצל',
  hiddenWhilePaused: 'מוסתר בזמן השהיה',
  letterInWord: '{letter}, אות {position} במילה',

  completeWord: 'השלימו את המילה',

  completeShort: 'השלם',
  reset: 'נקה',
  pause: 'השהה',
  resume: 'המשך',
  newGame: 'משחק חדש',
  paused: 'מושהה',
  outOfFlips: 'נגמרו המהלכים',
  finalResult: '{score} נקודות מתוך {words} במהלך {rounds}',
  playAgain: 'שחקו שוב',
  share: 'שתפו',
  shareCopied: 'הועתק.',
  shareSelect: 'העתיקו את זה:',

  lettersSelect: 'אותיות בוחרות',
  keysWild: 'נלקח כשמקלידים אות שאף אריח אינו מציג',
  clearsEvery: 'מנקה את כל האותיות {letter} שנבחרו',
  undoLastLetter: 'ביטול האות האחרונה',
  noWordsYet: 'עדיין אין מילים.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'כבר נמצאה',
  reasonTooShort: 'קצרה מדי',
  reasonNotAWord: 'אינה מילה',
  reasonAllFound: 'כבר מצאתם את כולן',
  noSuchLetterUp: 'אין {letter} גלויה',
  nothingUp: 'לא גלוי',
  letterHidden: 'אות הסתתרה',
  shuffled: 'עורבב',
  shuffledAndBilled: 'עורבב, חויבתם ב־{flips} מהלכים שלא נוצלו',
  tooFewLetters: 'נשארו מעט מדי אותיות',

  gameLanguage: 'שפה',
  interfaceLanguage: 'ממשק',
  dictionarySize: '{common} מילים שכיחות מתוך {full}',
  filterLanguages: 'חיפוש שפות',
  noMatches: 'אין תוצאות',

  nerdMode: 'מצב חנון',
  rules: 'חוקים',
  difficulty: 'רמת קושי',
  difficultyNames: { easy: 'קל', medium: 'בינוני', hard: 'קשה', insane: 'מטורף' },
  pausedNoBoard: 'המשחק הזה לא יופיע בטבלת השיאים.',
  about: 'אודות',
  themeLabel: 'ערכת נושא',
  themeNames: { traditional: 'מסורתית', light: 'בהירה', contrast: 'ניגודיות גבוהה' },
  tiles: 'אריחים (N)',
  secondsPerTick: 'שניות / פעימה',
  holdTicks: 'פעימות המתנה',
  minWord: 'המילה הקצרה ביותר',
  startingFlips: 'מהלכי פתיחה',
  wildChance: "סיכוי לג'וקר",
  replaceChance: 'סיכוי להחלפת אות',
  hideChance: 'סיכוי להסתרת אות',
  wordCompleteMode: 'בסיום מילה',
  wordCompleteNames: { shuffle: 'ערבב', spend: 'נצל', keep: 'שמור' },
  flipEconomy: 'כלכלת המהלכים',
  flipEconomyNames: {
    none: 'ללא',
    perLetter: 'לפי אות',
    fibonacci: 'פיבונאצ׳י',
    overMinimum: 'מעל המינימום',
  },
  repeatedLetterKey: 'מקש לאות חוזרת',
  keySchemeNames: { cycle: 'מחזורי', advance: 'מתקדם' },
  keySchemeHelp: {
    cycle:
      'א׳ לוקחת את הא׳ הפנויה הבאה, וכשכולן כבר במילה היא מנקה אותן. ' + 'גם Shift+א׳ מנקה אותן.',
    advance: 'א׳ לוקחת את הא׳ הפנויה הבאה. Shift+א׳ מנקה כל א׳ שבמילה.',
  },

  whatThatMeans: 'מה זה אומר',
  factRound: 'סיבוב',
  factWholeBoardUp: 'הלוח כולו גלוי למשך',
  factRoundCosts: 'סיבוב עולה',
  factFlipsBuy: 'מהלכי הפתיחה קונים',
  factThisBoard: 'הלוח הזה',
  factBoardHadToAdmit: 'הלוח היה חייב להתיר',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, הארוכה ביותר {longest}',
  wordsIncludingOneOf: '{words} ובהן אחת מתוך {ceiling}',
  scorelessRounds: '{rounds} ללא ניקוד',

  whatAWordPays: 'כמה שווה מילה',
  columnLetters: 'אותיות',
  columnCost: 'עלות',
  columnPoints: 'נקודות',
  columnFlips: 'מהלכים',
  columnNet: 'נטו',

  canonicalRules: 'חוקים רגילים לרמת {difficulty}.',
  customRules: 'שונה מהברירה. ניקוד תחת חוקים משלכם לא ידורג.',
  applyAndStart: 'החילו והתחילו משחק חדש',
  changesNextGame: 'השינויים ייכנסו לתוקף במשחק הבא.',
  presets: 'ברירות:',

  start: 'התחילו',
  restart: 'התחילו מחדש',
  quit: 'צאו',
  quitTitle: 'לצאת מהמשחק הזה?',
  restartTitle: 'להתחיל את המשחק הזה מחדש?',
  restartConfirm: 'התחל מחדש',
  quitConfirm: 'צא',
  keepPlaying: 'המשיכו לשחק',
  personalBest: 'המשחקים הטובים שלכם',
  newPersonalBest: 'שיא אישי חדש.',
  columnRank: '#',
  notRanked: 'חוקים משלכם, ולכן המשחק הזה אינו מדורג.',
  notRankedPaused: 'השעון נעצר, ולכן המשחק הזה אינו מדורג.',
  rankOfTotal: '{rank} מתוך {total}',

  howToPlay: 'איך משחקים',

  backToGame: 'חזרה למשחק',
  welcomeTitle: 'ברוכים הבאים ל־Blinkered',
  tutorialSkip: 'דלג',
  tutorialNext: 'הבא',
  tutorialBack: 'חזרה',
  tutorialStart: 'התחילו לשחק',
  tutorialHideAgain: 'אל תציגו שוב',
  tutorialProgress: '{n} מתוך {total}',
  tutorialSkipTitle: 'לדלג על ההדרכה?',
  tutGoalTitle: 'Blinkered הוא מרוץ',
  tutGoalBody:
    'אתם מתחילים את המשחק עם מספר קבוע של מהלכים, וכל אריח שמתהפך עולה מהלך אחד. כשהמהלכים נגמרים, המשחק נגמר. מילים מכניסות מהלכים, וככל שהמילה ארוכה יותר, תרוויחו יותר מהלכים.',
  tutSpentBody:
    'אותיות שהשתמשתם בהן במילה מתהפכות עד הסיבוב הבא. רק החלפת אות משנה אילו אותיות נמצאות על הלוח.',
  tutChangeTitle: 'אותיות יכולות להשתנות',
  tutSameLetters:
    'ראיתם מילה שלא הספקתם לשחק? אותן אותיות חוזרות בסיבוב הבא, מעורבבות במקומות חדשים.',
  tutPickLetters: 'הקישו על האותיות שאתם רוצים, לפי הסדר, כדי להרכיב מילה.',
  tutMoreTurn: 'האריחים ממשיכים להתהפך בזמן שאתם חושבים, אז אות טובה יותר עוד עשויה להגיע.',
  tutTapBack: 'הקשתם על אחת שלא רציתם? הקישו עליה שוב כדי להחזיר. על כל אחת, לא רק על האחרונה.',
  tutComplete: 'לחצו על השלם כשהמילה מוכנה.',
  htBoardTitle: 'הלוח',
  htBoardBody: 'האריחים נחשפים אחד אחרי השני, בסדר אקראי. מהאריחים הגלויים אתם מרכיבים מילים.',
  htWordsTitle: 'המילים',
  htWordsBody: 'הרכיבו מילה מהאריחים הגלויים בהקלדה או בלחיצה על האותיות לפי הסדר.',
  htFlipsTitle: 'המהלכים',
  htFlipsBody:
    'כל אריח שמתהפך עולה מהלך אחד. מילה שהושלמה מחזירה מהלכים לסך שלכם, ומילים ארוכות משלמות יותר. כשהמהלכים נגמרים, המשחק נגמר.',
  htRoundTitle: 'הסיבוב',
  htRoundBody:
    'כשהאריח האחרון של הסיבוב מתהפך, הלוח כולו נשאר גלוי לרגע. אחר כך האריחים מתהפכים בחזרה ומעורבבים, וסיבוב חדש מתחיל.',
  htLanguagesTitle: 'השפות',
  htLanguagesBody:
    '{n} שפות. כל לוח ניתן לפתרון במילים שאנשים באמת משתמשים בהן. מילה חריגה עדיין מזכה בניקוד, אם המילון מכיר אותה.',
  htKeysTitle: 'המקלדת',
  htWildTitle: "ג'וקרים",
  htWildBody:
    "לפעמים מופיע ג'וקר במקום אות. ג'וקר נחשב לכל אות שמרכיבה מילה תקינה. מילה שכבר השלמתם אינה נחשבת.",
  htHideTitle: 'אותיות שמסתתרות',
  htHideBody:
    'ברמות הקשות אות גלויה יכולה להתהפך בעצמה ולחזור בהמשך הסבב. הטיימר מקבל פעימה נוספת בשל כך, באדום. אותיות שכבר בחרת אינן מתהפכות.',
  tutHideBody:
    'ברמות הקשות אות גלויה יכולה להתהפך בעצמה ולחזור בהמשך הסבב. אותיות שכבר בחרת אינן מתהפכות.',
  htSwapTitle: 'אותיות מתחלפות',
  htSwapBody: 'לפעמים, בין הסיבובים, אות אחת מוחלפת באחרת. תראו איזו אות הוסרה ואיזו נוספה.',
  htLevelsTitle: 'הרמות',
  htLevelEasy:
    'אותן שתים עשרה אותיות לאורך כל המשחק, כך שאפשר ללמוד אותן ולהחזיק רשימת מילים בראש. האריחים מתהפכים לאט, והלוח המלא נשאר גלוי מספיק זמן כדי לסיים לבחור את האותיות.',
  htLevelMedium:
    'מדי פעם אות משתנה, וקשה יותר לזכור מילים שתכננתם לשחק בהמשך. פחות זמן להסתכל ופחות זמן לחשוב.',
  htLevelHard:
    'מילים בנות שלוש אותיות כבר לא נחשבות, ואות משתנה בערך בכל סיבוב שני. הלוח בקושי נחשף לפני שהוא מתערבב.',
  htLevelInsane: 'הכול ביחד, במהירות מלאה. הלוח מתערבב כמעט מיד אחרי המהלך האחרון.',
  htTouchTitle: 'מסך המגע',
  htTouchBody:
    'הקישו על אריח גלוי כדי לקחת את האות שלו. הקישו על כל אות שלקחתם כדי להחזיר אותה. השלם ונקה נמצאים מתחת ללוח.',

  /*
   * The numeral goes after the noun for one and before it for everything else, which is
   * Hebrew rather than a slip: מילה אחת is "one word" and 3 מילים is "3 words". Reading the
   * source is no help either way, since a terminal renders these lines right to left.
   */
  accountTitle: 'החשבון שלך',
  menuProfile: 'הפרופיל שלי',
  menuGames: 'המשחקים שלי',
  menuPublicPage: 'הדף הציבורי שלי',
  menuSignedInAs: 'מחובר בתור',
  signIn: 'התחברות',
  signOut: 'התנתקות',
  signInTitle: 'התחבר או הירשם',
  signInLead: 'חשבון שומר את המשחקים שלך, בכל מכשיר.',
  signInKeepGame: 'התחבר כדי לשמור את המשחק הזה, ואת כל הבאים.',
  continueWithApple: 'המשך עם Apple',
  continueWithGoogle: 'המשך עם Google',
  signInOr: 'או',
  emailLabel: 'אימייל',
  codeLabel: 'קוד',
  emailMeACode: 'שלחו לי קוד',
  working: 'רגע…',
  sendNewCode: 'שליחת קוד חדש',
  codeSent: 'בדוק את האימייל. הקוד תקף 10 דקות.',
  notNow: 'לא עכשיו',
  badEmail: 'זה לא נראה כמו כתובת אימייל.',
  badCode: 'הקוד לא עבד. קודים פגים אחרי 10 דקות ותקפים פעם אחת.',
  serverBusy: 'לא הצלחנו להגיע לשרת. נסה שוב בעוד רגע.',
  serverDown: 'לא הצלחנו להגיע לשרת.',
  offline: 'לא מחובר',
  leaderboardTitle: 'טבלת המובילים',
  leaderboardEmpty: 'אין עדיין משחקים בטבלה הזו. היה הראשון.',
  leaderboardWouldBe: 'איפה המשחק הזה היה מדורג',
  leaderboardThisGame: 'המשחק הזה',
  leaderboardRanksHere: 'איפה המשחק הזה מדורג',
  leaderboardOutside: 'מחוץ לחמישייה המובילה.',
  tutAccountTitle: 'שמור את הניקוד שלך',
  tutAccountBody:
    'חשבון שומר את המשחקים שלך, מכניס את הניקוד שלך לטבלת המובילים, ושומר את הפרופיל שלך.',
  ssoCancelled: 'ההתחברות בוטלה.',
  ssoExpired: 'זה לקח יותר מדי זמן. התחבר שוב.',
  ssoNoUsername: 'לא הצלחנו לסיים ליצור את החשבון. נסה שוב בעוד רגע.',
  ssoFailed: 'זה לא עבד. נסה שוב, או השתמש באימייל.',
  tabProfile: 'פרופיל',
  countryAny: 'מעדיף לא לומר',
  findCountry: 'חיפוש מדינות',
  usernameLabel: 'שם משתמש',
  bioLabel: 'על עצמי',
  countryLabel: 'מדינה',
  bioHint: 'בלי קישורים. {left}/{max}',
  save: 'שמירה',
  saving: 'שומר…',
  saved: 'נשמר.',
  nameTaken: 'השם הזה כבר תפוס.',
  nameTooShort: 'קצת יותר ארוך, בבקשה.',
  nameTooLong: 'ארוך מדי.',
  nameBadCharacters: 'רק אותיות, ספרות, מקפים וקווים תחתונים.',
  nameBadEdges: 'חייב להתחיל ולהסתיים באות או בספרה.',
  nameMixedScripts: 'בחר כתב אחד והישאר בו.',
  nameReserved: 'זה נראה כמו שם שאנחנו מחלקים. בחר אחר.',
  nameUnusable: 'אי אפשר להשתמש בשם הזה.',
  bioTooLong: 'השדה מכיל לכל היותר {max} תווים.',
  bioHasLink: 'קישורים אסורים כאן.',
  bioHasControl: 'יש כאן תווים שהשדה לא יכול להכיל.',
  bioUnusable: 'אי אפשר להשתמש בטקסט הזה.',
  gamesHeading: 'משחקים',
  gamesEmpty: 'עדיין אין כאן כלום. משחקים שתשחק כשאתה מחובר יופיעו כאן.',
  gamesEmptyShort: 'עדיין אין כאן כלום.',
  gamesLoading: 'קורא את המשחקים שלך…',
  theirGamesLoading: 'קורא את המשחקים שלהם…',
  profileLoading: 'קורא את הפרופיל…',
  playerNotFound: 'אין כאן אף אחד בשם הזה.',
  columnWhen: 'מתי',
  columnRounds: 'סיבובים',
  columnGame: 'משחק',
  gameLoading: 'קורא את המשחק…',
  gameUnreadable: 'לא הצלחנו לקרוא את המשחק הזה.',
  gameNotRanked: 'שוחק בחוקים ששונו, ולכן אינו מדורג.',
  detailNotKept: 'המילים והלוחות של המשחק הזה כבר לא נשמרים.',
  openingBoard: 'לוח הפתיחה',
  roundByRound: 'סיבוב אחר סיבוב',
  roundNothing: 'לא נמצא דבר',
  changeReplaced: '{letter} הוחלפה במשבצת {slot}',
  changeWildOn: '{letter} ג׳וקר במשבצת {slot}',
  changeWildOff: '{letter} ג׳וקר הוסר ממשבצת {slot}',
  keepThisGame: 'שמור את המשחק הזה',
  savedToYourGames: 'נשמר במשחקים שלך.',

  reportThis: 'דווח',
  reportHeading: 'דווח על זה',
  reportTheName: 'השם',
  reportTheBio: 'התיאור',
  reportTheScore: 'הניקוד',
  reportWhy: 'מה לא בסדר בזה? לא חובה.',
  reportSend: 'שליחת הדיווח',
  reportSent: 'תודה. מישהו יבדוק את זה.',
  reportSignIn: 'התחבר כדי לדווח על משהו.',
  reportGone: 'זה לא נמצא שם יותר.',
  reportCancel: 'ביטול',

  deleteAccount: 'מחק את החשבון שלי',
  deleteAccountWhat: 'זה מוחק את הפרופיל שלך וכל משחק ששמרת, לתמיד. אין דרך לבטל.',
  deleteAccountAsk: 'שלחו לי קוד',
  deleteConfirm: 'מחק לתמיד',
  deleteGone: 'החשבון שלך נמחק. תודה ששיחקת.',
  deleteNoAddress:
    'אין כתובת מאומתת בחשבון הזה, ולכן לא ניתן לאמת מחיקה. פנו אלינו ונעשה זאת ידנית.',

  rulesetFacts: 'מילים של {letters} · אריח חדש כל {seconds}',
  plurals: {
    words: { one: 'מילה {n}', two: '{n} מילים', other: '{n} מילים' },
    rounds: { one: 'סיבוב {n}', two: '{n} סיבובים', other: '{n} סיבובים' },
    flips: { one: 'מהלך {n}', two: '{n} מהלכים', other: '{n} מהלכים' },
    ticks: { one: 'פעימה {n}', two: '{n} פעימות', other: '{n} פעימות' },
    points: { one: 'נקודה {n}', two: '{n} נקודות', other: '{n} נקודות' },
    minLetters: { one: 'אות {n} ומעלה', two: '{n} אותיות ומעלה', other: '{n} אותיות ומעלה' },
    seconds: { one: '{n} שניות', two: '{n} שניות', other: '{n} שניות' },
  },
}
